import axios from "axios";
import _ from "lodash";
import { API_URL, STATS_ENABLE, STATS_URL } from "../consts";
import {
  ISdFpath,
  IRoadLayoutConfig,
  ILightingClassConfig,
  CClassEavTargetValuesEnum,
  CClassFTiTargetValuesEnum,
  CClassUoTargetValuesEnum,
  MClassFTiTargetValuesEnum,
  MClassLavTargetValuesEnum,
  MClassReiTargetValuesEnum,
  MClassUlTargetValuesEnum,
  MClassUoTargetValuesEnum,
  PClassEavTargetValuesEnum,
  PClassEminTargetValuesEnum,
  PClassEscminTargetValuesEnum,
  PClassEvminTargetValuesEnum,
  PClassFTiTargetValuesEnum,
  IProjectConfig,
  CustomClassModalData,
  CustomClassData,
  MCustomClassData,
  CCustomClassData,
  PCustomClassData,
} from "../types";

export function roundToTwo(num: number): number {
  return +(Math.round(Number.parseFloat(num.toString() + "e+2")) + "e-2");
}

export const getStepId = (stepIds: string[], step: string): number => {
  const idx = stepIds.indexOf(step);
  return idx !== -1 ? idx + 1 : 1;
};

export const getPreviousStepId = (stepIds: string[], step: string): string => {
  const idx = stepIds.indexOf(step);
  return idx > 0 ? stepIds[idx - 1] : stepIds[0];
};

export const sdFpathFind = (obj: undefined | ISdFpath[], position: string): null | ISdFpath => {
  if (typeof obj === "undefined") return null;
  if (!Array.isArray(obj)) return null;

  for (let i = 0; i < obj.length; i++) {
    if (obj[i].name === position) return obj[i];
  }
  return null;
};

export const subValList = (obj: undefined | ISdFpath[]): null | string[] => {
  if (typeof obj === "undefined") return null;
  if (!Array.isArray(obj)) return null;
  return obj.map((i) => i.name);
};

export const metMessage = (roadLayoutConfig: IRoadLayoutConfig, lightingClassConfig: ILightingClassConfig, met: any): string => {
  const { borderAreaLeft, borderAreaRight } = roadLayoutConfig;
  const { roadClasses, borderAreaLeftClasses, borderAreaRightClasses, customClasses } = lightingClassConfig;
  const leftCustomClass = customClasses?.leftBorderCustomClass;
  const rightCustomClass = customClasses?.rightBorderCustomClass;
  const showClassMessage = customClasses?.mainCustomClass?.class_type ? `${customClasses?.mainCustomClass?.class_type} Custom` : roadClasses;
  const showLwClassMessage = leftCustomClass?.class_type ? `${leftCustomClass?.class_type} Custom` : borderAreaLeftClasses;
  const showRwClassMessage = rightCustomClass?.class_type ? `${rightCustomClass?.class_type} Custom` : borderAreaRightClasses;
  const result = `The standard ${showClassMessage}${borderAreaLeft ? "/ " + showLwClassMessage + " (left sidewalk)" : ""} ${
    borderAreaRight ? "/ " + showRwClassMessage + " (right sidewalk)" : ""
  }is ${met === "Met" ? "met." : "not met (" + met + ")."}`;
  console.log(result);
  return result;
};

export const sendStatistics = (event_type: string, user_email: string, request_hash: string) => {
  if (STATS_ENABLE && user_email) {
    const url = `${STATS_URL}`;
    const reportName = (document.getElementById("report-name") as HTMLInputElement).value;
    const reportLocation = (document.getElementById("report-location") as HTMLInputElement).value;
    const reportComment = (document.getElementById("report-comment") as HTMLInputElement).value;
    const data = {
      timestamp: (Date.now() / 1000) | 0,
      payload: {
        request_hash: request_hash,
        project_name: reportName,
        project_location: reportLocation,
        project_comment: reportComment,
      },
      channel: "signify",
      event_type: event_type,
      user_id: 0,
      user_email: user_email,
      type: "event",
    };

    axios.post(url, data);
  }
};

export const getResultFormData = () => {
  const reportId = (document.getElementById("report-id") as HTMLInputElement)?.value;
  const reportName = (document.getElementById("report-name") as HTMLInputElement)?.value;
  const reportLocation = (document.getElementById("report-location") as HTMLInputElement)?.value;
  const reportRoadName = (document.getElementById("report-name_of_road") as HTMLInputElement)?.value;
  const reportComment = (document.getElementById("report-comment") as HTMLInputElement)?.value;

  return {
    reportId,
    reportName,
    reportLocation,
    reportRoadName,
    reportComment,
  };
};

export const fetchInitialData = async (callback: any, errorCallback: any, portfolioId: string) => {
  const url = `${API_URL}/api/portfolios/${portfolioId}/families/?format=json`;
  const returnedData = await axios.get(url).catch((error) => errorCallback(error.toJSON()));
  callback(returnedData);
};

/**
 * Retrieves the target values for a given class type and class name.
 *
 * @param classType - The type of the class ('M', 'C', or 'P').
 * @param className - The name of the class.
 * @returns A record containing the target values for the specified class type and name, or null if the class type is not recognized.
 *
 * @example
 * ```typescript
 * const targetValues = getClassTargetValues('M', 'M0');
 * if (targetValues) {
 *   console.log(targetValues.L̄m); // Access the L̄m target value for M class
 * }
 * ```
 */
export const getClassTargetValues = (classType: string, className: string): Record<string, string> | null => {
  switch (classType) {
    case "M":
      return {
        L̄m: MClassLavTargetValuesEnum[className as keyof typeof MClassLavTargetValuesEnum],
        Uo: MClassUoTargetValuesEnum[className as keyof typeof MClassUoTargetValuesEnum],
        Ul: MClassUlTargetValuesEnum[className as keyof typeof MClassUlTargetValuesEnum],
        fTI: MClassFTiTargetValuesEnum[className as keyof typeof MClassFTiTargetValuesEnum].replace("%", ""),
        REI: MClassReiTargetValuesEnum[className as keyof typeof MClassReiTargetValuesEnum],
      };
    case "C":
      return {
        Ēm: CClassEavTargetValuesEnum[className as keyof typeof CClassEavTargetValuesEnum],
        Uo: CClassUoTargetValuesEnum[className as keyof typeof CClassUoTargetValuesEnum],
        fTI: CClassFTiTargetValuesEnum[className as keyof typeof CClassFTiTargetValuesEnum].replace("%", ""),
      };
    case "P":
      return {
        Ēm: PClassEavTargetValuesEnum[className as keyof typeof PClassEavTargetValuesEnum],
        Ēmin: PClassEminTargetValuesEnum[className as keyof typeof PClassEminTargetValuesEnum],
        "Ev,min": PClassEvminTargetValuesEnum[className as keyof typeof PClassEvminTargetValuesEnum],
        "Esc,min": PClassEscminTargetValuesEnum[className as keyof typeof PClassEscminTargetValuesEnum],
        fTI: PClassFTiTargetValuesEnum[className as keyof typeof PClassFTiTargetValuesEnum].replace("%", ""),
      };
    default:
      return null;
  }
};

export function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

export const ceilToTwoDecimals = (value: string): string => {
  // Parse the input to a float
  const numericValue = Number.parseFloat(value);

  // If not a valid number, return the original value
  if (Number.isNaN(numericValue)) return value;

  // Ceil the value to two decimal places
  const ceiledValue = Math.ceil(numericValue * 100) / 100;

  // Return the value formatted with two decimal places
  return ceiledValue.toFixed(2);
};

export const generatePdfFileName = (config: IProjectConfig, defaultName: string): string => {
  const { project_name, location, project_id, name_of_road } = config;

  if (project_name && location && project_id && name_of_road) {
    return `${project_name} - ${location} - ${project_id} - ${name_of_road}`;
  }

  return defaultName;
};

export const generateFormExistingData = (data: CustomClassModalData, isMain: boolean, isLeft?: boolean) => {
  const { mainCustomClass, rightBorderCustomClass, leftBorderCustomClass } = data;

  const formData: Record<string, string> = {};

  if (isMain && mainCustomClass) {
    // biome-ignore lint/complexity/noForEach: <explanation>
    Object.keys(mainCustomClass).forEach((key) => {
      formData[key] = mainCustomClass[key].toString();
    });
  }

  if (rightBorderCustomClass && !isMain && !isLeft) {
    // biome-ignore lint/complexity/noForEach: <explanation>
    Object.keys(rightBorderCustomClass).forEach((key) => {
      formData[key] = rightBorderCustomClass[key].toString();
    });
  }

  if (leftBorderCustomClass && !isMain && isLeft) {
    // biome-ignore lint/complexity/noForEach: <explanation>
    Object.keys(leftBorderCustomClass).forEach((key) => {
      formData[key] = leftBorderCustomClass[key].toString();
    });
  }
  const preFilledData = transformCustomClassData(formData as CustomClassData);
  // biome-ignore lint/performance/noDelete: <explanation>
  delete preFilledData.class_type;
  return preFilledData;
};

export const isCustomClassModalDataEmpty = (data: CustomClassModalData, isMain: boolean, isLeft?: boolean): boolean => {
  const { mainCustomClass, rightBorderCustomClass, leftBorderCustomClass } = data;

  const isEmpty = (obj: Record<string, unknown> | undefined): boolean => {
    return !obj || Object.keys(obj).length === 0;
  };

  if (isMain) {
    return isEmpty(mainCustomClass);
  }

  if (isLeft) {
    return isEmpty(leftBorderCustomClass);
  }

  return isEmpty(rightBorderCustomClass);
};

function transformCustomClassData(data: CustomClassData): Record<string, string> {
  if (data.class_type === "M") {
    const { Lm_av, L_uo, L_ul, fTI, Rei } = data as MCustomClassData;
    return {
      L̄m: Lm_av.toString(),
      Uo: L_uo.toString(),
      Ul: L_ul.toString(),
      fTI: fTI.toString(),
      REI: Rei.toString(),
    };
  }
  if (data.class_type === "C") {
    const { Em_av, E_uo, fTI } = data as CCustomClassData;
    return {
      Ēm: Em_av.toString(),
      Uo: E_uo.toString(),
      fTI: fTI.toString(),
    };
  }
  if (data.class_type === "P") {
    const { Em_av, Em_min, fTI, Evm_min, Escm_min } = data as PCustomClassData;
    return {
      Ēm: Em_av.toString(),
      Ēmin: Em_min.toString(),
      "Ev,min": Evm_min.toString(),
      "Esc,min": Escm_min.toString(),
      fTI: fTI.toString(),
    };
  }
  throw new Error("Unsupported class type");
}

/**
 * Finds the correct family and image for a luminaire based on its article ID and name
 *
 * @param articleId - The article ID of the luminaire
 * @param name - The name/ldc_id of the luminaire
 * @param families - The families data from the API
 * @param fallbackFamilyIndex - Optional fallback index if no match is found
 * @param apiUrl - The API URL prefix for image paths
 * @returns An object containing the family index, family ID, and image URL
 */
export const findLuminaireFamily = (
  articleId: string,
  name: string,
  families: any,
  fallbackFamilyIndex?: number,
  apiUrl: string | undefined = API_URL
): { familyIndex: number; familyId: string; imageUrl: string } => {
  let correctFamilyIndex = -1;
  let correctFamilyImage = "";
  let correctFamilyId = "";

  if (families && families.data && families.data.families) {
    // First try to find by exact article ID match
    correctFamilyIndex = _.findIndex(families.data.families, (family: any) => {
      const hasArticleId = family.article_ids?.includes(articleId) || family.id === articleId;
      return hasArticleId;
    });

    // If not found, try to match by model name patterns
    if (correctFamilyIndex === -1 && name) {
      // Try different patterns to extract model name
      let modelName = "";

      // Pattern 1: "Cuvia 60" from "Cuvia 60-AB5L_4200-740 8G1.ldt"
      const cuviaMatch = name.match(/^(Cuvia \d+)/);
      if (cuviaMatch) modelName = cuviaMatch[1];

      // Pattern 2: "9711" from "9711SG-AB5L_4200-740 8G2.ldt" or "9701" from "9701SG-AB5L_4200-740 8G2.ldt"
      if (!modelName) {
        const lumegaMatch = name.match(/^(\d{4})[A-Z]+/);
        if (lumegaMatch) modelName = lumegaMatch[1];
      }

      // Original pattern for other models (e.g., "BDP510")
      if (!modelName) {
        const modelMatch = name.match(/^([A-Z]{3}\d{3})/);
        if (modelMatch) modelName = modelMatch[1];
      }

      if (modelName) {
        correctFamilyIndex = _.findIndex(families.data.families, (family: any) => {
          return family.name.includes(modelName);
        });
      }
    }

    // If we found a matching family, get its image and ID
    if (correctFamilyIndex !== -1) {
      correctFamilyImage = apiUrl + families.data.families[correctFamilyIndex].image;
      correctFamilyId = families.data.families[correctFamilyIndex].id;
    } else if (fallbackFamilyIndex !== undefined && fallbackFamilyIndex > -1) {
      // Fall back to the provided familyIndex if no specific match found
      correctFamilyImage = apiUrl + families.data.families[fallbackFamilyIndex].image;
      correctFamilyId = families.data.families[fallbackFamilyIndex].id;
    }
  }

  return {
    familyIndex: correctFamilyIndex !== -1 ? correctFamilyIndex : fallbackFamilyIndex || -1,
    familyId: correctFamilyId,
    imageUrl: correctFamilyImage,
  };
};

/**
 * Downloads a PDF file from a Blob object
 *
 * @param pdf - The PDF Blob object to download
 * @param fileName - The name of the file to download (default: 'report.pdf')
 *
 * @example
 * ```typescript
 * const { pdf } = useGeneratePdf();
 * if (pdf) {
 *   downloadPdf(pdf, 'lighting-report.pdf');
 * }
 * ```
 */
export const downloadPdf = (pdf: Blob, fileName = "report.pdf"): void => {
  const url = globalThis.URL.createObjectURL(pdf);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
